
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import coachService from '../services/endpoints/coachService';
import { getAthleteTrainingActivity, endAssignment } from '../services/endpoints/trainingService';
import { ClientSummary, WeightPoint } from '../types';
import type { AthleteTrainingActivity } from '../types/training';
import { parseLocalDate } from '../utils/dateUtils';
import CompetitionCalendar from '../components/coach/CompetitionCalendar';
import CoachAthleteNotesDrawer from '../components/coach/CoachAthleteNotesDrawer';
import AthleteCheckInDrawer from '../components/coach/AthleteCheckInDrawer';
import QuickMessageDrawer from '../components/coach/QuickMessageDrawer';
import CoachPlanFilesDrawer from '../components/coach/CoachPlanFilesDrawer';

const CoachDashboard: React.FC = () => {
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadClients = useCallback(async () => {
    try {
      const data = await coachService.getClientSummaries();
      setClients(data);
    } catch (err: any) {
      console.error('Failed to load client summaries', err);
      setError('Failed to load athlete data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading coach dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gold-500">Coach Dashboard</h1>
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">{clients.length} athlete{clients.length !== 1 ? 's' : ''}</span>
          <Link
            to="/settings?tab=clients"
            className="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-black text-sm font-semibold rounded-lg transition-colors"
          >
            + Add an Athlete
          </Link>
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-4xl mb-4">🏋️</div>
          <h2 className="text-xl text-gray-300 mb-2">No Athletes Connected</h2>
          <p className="text-gray-500 text-center max-w-md">
            Go to <a href="/settings" className="text-gold-500 hover:text-gold-400">Settings → My Athletes</a> to
            redeem an invite code from a bodybuilder.
          </p>
        </div>
      ) : (
        <AthletesPanel clients={clients} />
      )}
    </div>
  );
};

// ─── Main Athletes Panel ─────────────────────────────────────────────────────

const AthletesPanel: React.FC<{ clients: ClientSummary[] }> = ({ clients: initialClients }) => {
  const navigate = useNavigate();
  const now = new Date();

  // Local copy so we can update hasNotes in-place without refetching summaries
  const [clients, setClients] = useState<ClientSummary[]>(initialClients);
  useEffect(() => { setClients(initialClients); }, [initialClients]);

  const [notesTarget, setNotesTarget]             = useState<ClientSummary | null>(null);
  const [checkInTarget, setCheckInTarget]         = useState<ClientSummary | null>(null);
  const [quickMessageTarget, setQuickMessageTarget] = useState<ClientSummary | null>(null);
  const [planFilesTarget, setPlanFilesTarget]     = useState<ClientSummary | null>(null);

  // ── Roster Pulse stats ────────────────────────────────────────────────────
  const checkedInLast7 = clients.filter((c) => {
    if (!c.lastCheckInDate) return false;
    const diff = (now.getTime() - new Date(c.lastCheckInDate).getTime()) / (1000 * 60 * 60 * 24);
    return diff <= 7;
  }).length;

  const withWellbeing = clients.filter((c) => c.wellbeingScore != null);
  const avgWellbeing = withWellbeing.length > 0
    ? (withWellbeing.reduce((s, c) => s + (c.wellbeingScore ?? 0), 0) / withWellbeing.length).toFixed(1)
    : null;

  const inContestPrep = clients.filter(
    (c) => c.currentPhaseType?.toLowerCase().includes('contest') || c.currentPhaseType?.toLowerCase().includes('prep')
  ).length;

  // ── Attention flags ───────────────────────────────────────────────────────
  const attentionClients = clients.filter((c) => {
    const daysSince = c.lastCheckInDate
      ? (now.getTime() - new Date(c.lastCheckInDate).getTime()) / (1000 * 60 * 60 * 24)
      : 999;
    const lowWellbeing   = c.wellbeingScore != null && c.wellbeingScore < 5;
    const overWeight     = c.latestWeight && c.targetWeight && c.latestWeight - c.targetWeight > 5;
    const bpElevated     = (c.latestBpSystolic != null && c.latestBpSystolic >= 130)
                         || (c.latestBpDiastolic != null && c.latestBpDiastolic >= 80);
    const highGlucose    = c.glucoseConsistentlyHigh;
    return lowWellbeing || overWeight || daysSince > 3 || bpElevated || highGlucose;
  });

  const goView = (c: ClientSummary) =>
    navigate(`/?viewAs=${c.athleteUserId}&viewAsName=${encodeURIComponent(c.athleteName)}&relationshipId=${c.relationshipId}`);

  return (
    <div className="space-y-6">

      {/* ── Roster Pulse ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <PulseCard label="Total Athletes" value={`${clients.length}`} icon="🏋️" color="text-gold-400" />
        <PulseCard
          label="Active (7d)"
          value={`${checkedInLast7} / ${clients.length}`}
          icon="✅"
          color={checkedInLast7 === clients.length ? 'text-green-400' : 'text-yellow-400'}
        />
        <PulseCard
          label="Avg Wellbeing"
          value={avgWellbeing ? `${avgWellbeing} / 10` : '—'}
          icon="❤️"
          color={avgWellbeing
            ? parseFloat(avgWellbeing) >= 7 ? 'text-green-400'
              : parseFloat(avgWellbeing) >= 5 ? 'text-yellow-400'
              : 'text-red-400'
            : 'text-gray-400'}
        />
        <PulseCard
          label="Contest Prep"
          value={`${inContestPrep}`}
          icon="🏆"
          color="text-orange-400"
        />
      </div>

      {/* ── Attention Flags ── */}
      {attentionClients.length > 0 && (
        <div className="bg-dark-800 rounded-xl border border-yellow-600/30 p-5">
          <h2 className="text-lg font-semibold text-yellow-400 mb-4">⚠️ Needs Attention</h2>
          <div className="space-y-3">
            {attentionClients.map((c) => {
              const daysSince = c.lastCheckInDate
                ? Math.floor((now.getTime() - new Date(c.lastCheckInDate).getTime()) / (1000 * 60 * 60 * 24))
                : null;
              const flags: { text: string; danger?: boolean }[] = [];
              if (daysSince === null)
                flags.push({ text: 'Never checked in' });
              else if (daysSince > 3)
                flags.push({ text: `No check-in for ${daysSince}d` });
              if (c.wellbeingScore != null && c.wellbeingScore < 5)
                flags.push({ text: `Low wellbeing (${c.wellbeingScore}/10)` });
              if (c.latestWeight && c.targetWeight && c.latestWeight - c.targetWeight > 5)
                flags.push({ text: `${(c.latestWeight - c.targetWeight).toFixed(1)} lbs over target` });
              if (c.latestBpSystolic != null && c.latestBpDiastolic != null) {
                if (c.latestBpSystolic >= 140 || c.latestBpDiastolic >= 90)
                  flags.push({ text: `BP High (${c.latestBpSystolic}/${c.latestBpDiastolic})`, danger: true });
                else if (c.latestBpSystolic >= 130 || c.latestBpDiastolic >= 80)
                  flags.push({ text: `BP Elevated (${c.latestBpSystolic}/${c.latestBpDiastolic})` });
              } else if (c.latestBpSystolic != null && c.latestBpSystolic >= 130)
                flags.push({ text: `BP Elevated (${c.latestBpSystolic}/—)` });
              if (c.glucoseConsistentlyHigh)
                flags.push({ text: 'Glucose >100 (3 readings)' });

              return (
                <div key={c.athleteUserId} className="flex items-center justify-between bg-dark-700 rounded-lg px-4 py-3">
                  <div>
                    <div className="text-white font-medium">{c.athleteName}</div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {flags.map((f, i) => (
                        <span
                          key={i}
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            f.danger
                              ? 'bg-red-900/40 text-red-300'
                              : 'bg-yellow-900/40 text-yellow-300'
                          }`}
                        >
                          {f.text}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => goView(c)}
                    className="text-xs text-gold-500 hover:text-gold-400 ml-4 shrink-0"
                  >
                    View →
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Competition Calendar ── */}
      <CompetitionCalendar />

      {/* ── Athlete Roster Table ── */}
      <div className="bg-dark-800 rounded-xl border border-dark-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-dark-700">
          <h2 className="text-lg font-semibold text-white">📋 Athlete Roster</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wider bg-dark-900/50">
                <th className="px-4 py-3 w-4" />
                <th className="px-4 py-3">Athlete</th>
                <th className="px-4 py-3">Show</th>
                <th className="px-4 py-3">Weeks Out</th>
                <th className="px-4 py-3">Phase</th>
                <th className="px-4 py-3">Weight</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Wellbeing</th>
                <th className="px-4 py-3">Last Check-In</th>
                <th className="px-4 py-3 min-w-[120px]">14d Trend</th>
                <th className="px-4 py-3 text-center">Notes</th>
                <th className="px-4 py-3 text-center text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {clients.map((c) => {
                const daysSince = c.lastCheckInDate
                  ? Math.floor((now.getTime() - new Date(c.lastCheckInDate).getTime()) / (1000 * 60 * 60 * 24))
                  : null;
                const weightDelta = c.latestWeight && c.targetWeight
                  ? (c.latestWeight - c.targetWeight)
                  : null;
                const freshnessDot = getFreshnessDot(c.lastCheckInDate);

                return (
                  <tr key={c.athleteUserId} className="hover:bg-dark-700/50 transition-colors">
                    <td className="px-4 py-3 text-center">{freshnessDot}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => setCheckInTarget(c)}
                        className="text-white font-medium hover:text-gold-400 transition-colors text-left"
                        title="Quick check-in view"
                      >
                        {c.athleteName}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {c.showName ? (
                        <div>
                          <div className="text-gray-300 whitespace-nowrap">{c.showName}</div>
                          {c.showDate && (
                            <div className="text-xs text-gray-500">
                              {parseLocalDate(c.showDate).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-600 italic text-xs">No show</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                      {c.weeksOut != null ? `${c.weeksOut}w ${c.daysRemainder || 0}d` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {c.currentPhaseName ? (
                        <div>
                          <div className="text-gray-300">{c.currentPhaseName}</div>
                          {c.currentPhaseType && (
                            <div className="text-xs text-gray-500">{c.currentPhaseType}</div>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                      {c.latestWeight ? `${c.latestWeight} lbs` : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {c.targetWeight ? (
                        <div>
                          <div className="text-gray-300">{c.targetWeight} lbs</div>
                          {weightDelta !== null && (
                            <div className={`text-xs font-medium ${weightDelta <= 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                              {weightDelta > 0 ? '+' : ''}{weightDelta.toFixed(1)} lbs
                            </div>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {c.wellbeingScore != null ? (
                        <span className={`font-medium ${
                          c.wellbeingScore >= 7 ? 'text-green-400'
                          : c.wellbeingScore >= 5 ? 'text-yellow-400'
                          : 'text-red-400'
                        }`}>
                          {c.wellbeingScore}/10
                        </span>
                      ) : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {daysSince === null ? (
                        <span className="text-gray-600">—</span>
                      ) : daysSince === 0 ? (
                        <span className="text-green-400">Today</span>
                      ) : (
                        <span className={daysSince <= 3 ? 'text-yellow-400' : 'text-red-400'}>
                          {daysSince}d ago
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.weightTrend && c.weightTrend.length > 1 ? (
                        <div className="w-28">
                          <WeightSparkline points={c.weightTrend} />
                        </div>
                      ) : (
                        <span className="text-gray-600 text-xs">No data</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setNotesTarget(c)}
                        title={c.hasNotes ? 'View private notes' : 'Add private note'}
                        className="text-lg leading-none transition-opacity hover:opacity-70"
                      >
                        {c.hasNotes ? (
                          <span className="text-gold-400">📝</span>
                        ) : (
                          <span className="text-gray-600">📝</span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <RowActionsMenu
                        onViewAthlete={() => goView(c)}
                        onCheckIn={() => setCheckInTarget(c)}
                        onQuickMessage={() => setQuickMessageTarget(c)}
                        onPlanFiles={() => setPlanFilesTarget(c)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Training Activity ── */}
      <TrainingActivitySection />

      {/* ── Coach Notes Drawer ── */}
      {notesTarget && (
        <CoachAthleteNotesDrawer
          athleteUserId={notesTarget.athleteUserId}
          athleteName={notesTarget.athleteName}
          relationshipId={notesTarget.relationshipId}
          onClose={() => setNotesTarget(null)}
          onNotesChanged={(hasNotes) => {
            setClients(prev =>
              prev.map(c =>
                c.athleteUserId === notesTarget.athleteUserId ? { ...c, hasNotes } : c
              )
            );
          }}
        />
      )}

      {/* ── Check-In Flyout ── */}
      {checkInTarget && (
        <AthleteCheckInDrawer
          athleteUserId={checkInTarget.athleteUserId}
          athleteName={checkInTarget.athleteName}
          relationshipId={checkInTarget.relationshipId}
          onClose={() => setCheckInTarget(null)}
        />
      )}

      {/* ── Quick Message Flyout ── */}
      {quickMessageTarget && (
        <QuickMessageDrawer
          athleteName={quickMessageTarget.athleteName}
          relationshipId={quickMessageTarget.relationshipId}
          onClose={() => setQuickMessageTarget(null)}
        />
      )}

      {/* ── Plan Files Flyout ── */}
      {planFilesTarget && (
        <CoachPlanFilesDrawer
          athleteUserId={planFilesTarget.athleteUserId}
          athleteName={planFilesTarget.athleteName}
          onClose={() => setPlanFilesTarget(null)}
        />
      )}
    </div>
  );
};

// ─── Training Activity Section (v3.3) ────────────────────────────────────────

/** Returns a color class + label for "days since last session" display. */
function getTrainingFreshness(days?: number): { dot: string; label: string; labelCls: string } {
  if (days === undefined || days === null)
    return { dot: 'bg-gray-600', label: 'Never logged', labelCls: 'text-gray-500' };
  if (days === 0)  return { dot: 'bg-green-500',  label: 'Today',             labelCls: 'text-green-400'  };
  if (days === 1)  return { dot: 'bg-green-400',  label: 'Yesterday',         labelCls: 'text-green-400'  };
  if (days <= 3)   return { dot: 'bg-yellow-500', label: `${days}d ago`,      labelCls: 'text-yellow-400' };
  if (days <= 7)   return { dot: 'bg-orange-500', label: `${days}d ago`,      labelCls: 'text-orange-400' };
  return               { dot: 'bg-red-500',    label: `${days}d ago`,          labelCls: 'text-red-400'    };
}

const TrainingActivitySection: React.FC = () => {
  const navigate = useNavigate();
  const [activity, setActivity] = useState<AthleteTrainingActivity[]>([]);
  const [endTarget, setEndTarget] = useState<AthleteTrainingActivity | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await getAthleteTrainingActivity();
      setActivity(data);
    } catch { /* no-op */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || activity.length === 0) return null;

  return (
    <div className="bg-dark-800 rounded-xl border border-dark-700 overflow-hidden">
      <div className="px-5 py-4 border-b border-dark-700 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">🏋️ Training Activity</h2>
        <Link to="/training/plans" className="text-xs text-gold-500 hover:text-gold-400">Manage Plans →</Link>
      </div>

      <div className="divide-y divide-dark-700">
        {activity.map(a => {
          const fresh = getTrainingFreshness(a.daysSinceLastSession);
          return (
            <div key={a.assignmentId} className="px-5 py-3 flex items-center gap-4 hover:bg-dark-700/30 transition-colors">
              {/* Freshness dot */}
              <span title={fresh.label} className={`shrink-0 inline-block w-2.5 h-2.5 rounded-full ${fresh.dot}`} />

              {/* Athlete + plan info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white text-sm font-medium">{a.athleteName}</span>
                  <span className="text-gray-500 text-xs">·</span>
                  <span className="text-gray-400 text-xs truncate">{a.planName}</span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                  <span className={`text-xs font-medium ${fresh.labelCls}`}>
                    {a.lastSessionDate
                      ? `Last: ${new Date(a.lastSessionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (${fresh.label})`
                      : fresh.label}
                  </span>
                  {a.lastSessionDayName && (
                    <span className="text-gray-500 text-xs">{a.lastSessionDayName}</span>
                  )}
                  <span className="text-gray-600 text-xs">
                    {a.totalSessions} session{a.totalSessions !== 1 ? 's' : ''} total
                  </span>
                  {a.lastSessionOverallRating != null && (
                    <span className="text-yellow-400 text-xs">
                      {'★'.repeat(a.lastSessionOverallRating)}{'☆'.repeat(5 - a.lastSessionOverallRating)}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => navigate(
                    `/training?viewAs=${a.athleteUserId}&viewAsName=${encodeURIComponent(a.athleteName)}` +
                    (a.relationshipId ? `&relationshipId=${a.relationshipId}` : '')
                  )}
                  className="text-xs px-3 py-1.5 bg-dark-600 hover:bg-dark-500 border border-dark-500 hover:border-gold-600/50 text-gray-300 hover:text-gold-300 rounded transition-colors"
                  title="View athlete's session history"
                >
                  View Sessions
                </button>
                <button
                  onClick={() => navigate(`/training/plans?planId=${a.trainingPlanId}&assignmentId=${a.assignmentId}`)}
                  className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                >
                  Edit →
                </button>
                <button
                  onClick={() => setEndTarget(a)}
                  className="text-xs px-2.5 py-1.5 bg-gray-700 hover:bg-red-900/60 text-gray-400 hover:text-red-300 rounded transition-colors"
                  title="Remove this plan from athlete's active training"
                >
                  🗑
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {endTarget && (
        <EndAssignmentModal
          activity={endTarget}
          onConfirmed={() => { setEndTarget(null); load(); }}
          onClose={() => setEndTarget(null)}
        />
      )}
    </div>
  );
};

// ─── End Assignment Modal ────────────────────────────────────────────────────

interface EndAssignmentModalProps {
  activity: AthleteTrainingActivity;
  onConfirmed: () => void;
  onClose: () => void;
}

const EndAssignmentModal: React.FC<EndAssignmentModalProps> = ({ activity, onConfirmed, onClose }) => {
  const [reason, setReason] = useState('');
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  const handleRemove = async () => {
    setRemoving(true);
    setError('');
    try {
      await endAssignment(activity.assignmentId, reason.trim() || undefined);
      onConfirmed();
    } catch {
      setError('Failed to remove plan. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-sm">
        <div className="px-6 pt-5 pb-3 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-white font-semibold text-lg">Remove Training Plan?</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg">✕</button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <p className="text-gray-300 text-sm">
            Remove <span className="font-medium text-white">"{activity.planName}"</span> from{' '}
            <span className="font-medium text-white">{activity.athleteName}</span>'s active training?
          </p>
          <div className="bg-green-900/20 border border-green-700/40 rounded-lg px-3 py-2 text-xs text-green-300">
            ✓ Session history is preserved. The athlete will be notified via message.
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Reason <span className="text-gray-600">(optional — shown to athlete)</span></label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Switching to a new program for your show prep…"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm resize-none focus:border-blue-500 focus:outline-none"
            />
          </div>
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
        <div className="px-6 pb-5 flex gap-3">
          <button
            onClick={handleRemove}
            disabled={removing}
            className="flex-1 py-2.5 bg-red-700 hover:bg-red-600 text-white rounded-lg font-medium text-sm disabled:opacity-50"
          >
            {removing ? 'Removing…' : 'Remove Plan'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Row Actions Menu ────────────────────────────────────────────────────────

interface RowActionsMenuProps {
  onViewAthlete: () => void;
  onCheckIn: () => void;
  onQuickMessage: () => void;
  onPlanFiles: () => void;
}

const RowActionsMenu: React.FC<RowActionsMenuProps> = ({ onViewAthlete, onCheckIn, onQuickMessage, onPlanFiles }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, right: 0 });
  const btnRef  = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        btnRef.current  && !btnRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="inline-block">
      <button
        ref={btnRef}
        onClick={handleOpen}
        title="Actions"
        className="text-gray-400 hover:text-white text-lg leading-none px-2 py-1 rounded hover:bg-dark-600 transition-colors"
      >
        ⋮
      </button>

      {open && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
          className="w-44 bg-dark-700 border border-dark-600 rounded-lg shadow-xl z-50 py-1"
        >
          <button
            onClick={() => { onViewAthlete(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-dark-600 hover:text-white transition-colors flex items-center gap-2"
          >
            <span>👤</span> View Athlete
          </button>
          <button
            onClick={() => { onCheckIn(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-dark-600 hover:text-white transition-colors flex items-center gap-2"
          >
            <span>📋</span> Latest Check-In
          </button>
          <button
            onClick={() => { onQuickMessage(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-dark-600 hover:text-white transition-colors flex items-center gap-2"
          >
            <span>💬</span> Quick Message
          </button>
          <button
            onClick={() => { onPlanFiles(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-dark-600 hover:text-white transition-colors flex items-center gap-2"
          >
            <span>📄</span> Plan Files
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Pulse Card ─────────────────────────────────────────────────────────────

const PulseCard: React.FC<{ label: string; value: string; icon: string; color: string }> = ({
  label, value, icon, color,
}) => (
  <div className="bg-dark-800 rounded-xl border border-dark-700 px-5 py-4 text-center">
    <div className="text-2xl mb-1">{icon}</div>
    <div className={`text-xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
  </div>
);

// ─── Freshness Dot ──────────────────────────────────────────────────────────

function getFreshnessDot(lastCheckInDate?: string): React.ReactNode {
  if (!lastCheckInDate) {
    return <span title="No check-ins" className="inline-block w-2.5 h-2.5 rounded-full bg-gray-600" />;
  }
  const daysDiff = Math.floor(
    (new Date().getTime() - new Date(lastCheckInDate).getTime()) / (1000 * 60 * 60 * 24)
  );
  if (daysDiff <= 0) return <span title="Today" className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" />;
  if (daysDiff <= 3) return <span title={`${daysDiff}d ago`} className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500" />;
  return <span title={`${daysDiff}d ago`} className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />;
}

// ─── SVG Sparkline ──────────────────────────────────────────────────────────

const WeightSparkline: React.FC<{ points: WeightPoint[] }> = ({ points }) => {
  const width = 120;
  const height = 36;
  const padding = 2;

  const weights = points.map(p => p.weight);
  const minW = Math.min(...weights);
  const maxW = Math.max(...weights);
  const range = maxW - minW || 1;

  const pathPoints = points.map((p, i) => {
    const x = padding + (i / (points.length - 1)) * (width - padding * 2);
    const y = height - padding - ((p.weight - minW) / range) * (height - padding * 2);
    return `${x},${y}`;
  });
  const polyline = pathPoints.join(' ');
  const firstX = padding;
  const lastX = padding + (width - padding * 2);
  const areaPath = `M${firstX},${height} L${polyline.split(' ').join(' L')} L${lastX},${height} Z`;

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="rounded">
      <defs>
        <linearGradient id="sparkGradR" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#D4A017" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#D4A017" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#sparkGradR)" />
      <polyline
        points={polyline}
        fill="none"
        stroke="#D4A017"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={lastX}
        cy={height - padding - ((weights[weights.length - 1] - minW) / range) * (height - padding * 2)}
        r="2.5"
        fill="#D4A017"
      />
    </svg>
  );
};

export default CoachDashboard;
